import React, { useCallback, useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { TicketSummary } from "@/lib/types/database";
import { useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { useTicketsPaginated } from "@/lib/hooks/api/tickets";
import {
  convertToTicketSummaryArray,
  processTicketsData,
  type RawTicketData,
} from "@/lib/utils/crm/ticket-helpers";
import { ResponsiveCommandSelect, SelectOption } from "@/components/ui/responsive-select";
import { TicketOptionPreview } from "./shared/TicketOptionPreview";
import { useAccumulatedList } from "./shared/useAccumulatedList";
import { useFTSWithDebouncedSearch } from "./shared/useFTSWithDebouncedSearch";

const QuickTicketCreationModal = dynamic(
  () =>
    import("@/features/time-entries/components/QuickTicketCreationModal").then(
      (mod) => mod.QuickTicketCreationModal
    ),
  { ssr: false }
);

interface TicketSelectProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  items?: TicketSummary[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  isSearching?: boolean;
  onSearchChange?: (query: string) => void;
  searchTerm?: string;
  fullWidth?: boolean;
  /** When true, expand inline (search + list below) instead of popover. Default true for tables/modals. Pass false for popover. */
  expandInline?: boolean;
  /** When expandInline: "floating" (default) = trigger stays, dropdown floats; "replace" = trigger replaced by panel */
  expandInlineVariant?: "replace" | "floating";
  [key: string]: unknown;
}

interface EnhancedTicketOption extends SelectOption {
  status?: string;
  priority?: string;
  contact_name?: string;
  assigned_to_name?: string;
}

const renderTicketOption = (option: SelectOption) => {
  const enhanced = option as EnhancedTicketOption;
  return (
    <TicketOptionPreview
      label={enhanced.label}
      status={enhanced.status}
      priority={enhanced.priority}
      contact_name={enhanced.contact_name}
      assigned_to_name={enhanced.assigned_to_name}
    />
  );
};

export function TicketSelectWithModal(props: TicketSelectProps) {
  const {
    searchValue: ticketSearchTerm,
    debouncedSearchValue: debouncedTicketSearchTerm,
    setSearchValue: setTicketSearchTerm,
    hasSearchTerm,
    ftsResults,
    ftsLoading,
  } = useFTSWithDebouncedSearch<unknown>({ entityType: "tickets" });

  const [page, setPage] = useState(1);
  const limit = 30;

  const {
    data: ticketsPage,
    isLoading: paginationLoading,
    isFetching: isBackgroundFetching,
  } = useTicketsPaginated({
    page,
    limit,
    search: hasSearchTerm ? undefined : debouncedTicketSearchTerm || undefined,
  });

  useEffect(() => {
    if (!hasSearchTerm) setPage(1);
  }, [hasSearchTerm]);

  const pageItems = useMemo(
    () => convertToTicketSummaryArray(ticketsPage?.data ?? []),
    [ticketsPage]
  );

  const accumulatedTickets = useAccumulatedList<TicketSummary>({
    page,
    firstPage: 1,
    items: pageItems,
    idOf: (t) => String(t.id),
    paused: hasSearchTerm,
  });

  const sourceTickets = useMemo((): TicketSummary[] => {
    if (hasSearchTerm) {
      const processed = processTicketsData(ftsResults as unknown as RawTicketData[]);
      return convertToTicketSummaryArray(processed);
    }
    return accumulatedTickets.length > 0 ? accumulatedTickets : pageItems;
  }, [hasSearchTerm, ftsResults, accumulatedTickets, pageItems]);

  const globalModalStack = useGlobalModalStack();

  const {
    value,
    onValueChange,
    items,
    placeholder,
    disabled,
    isSearching,
    onSearchChange,
    searchTerm: searchTermProp,
    fullWidth,
    expandInline = true,
    expandInlineVariant = "floating",
  } = props;

  const handleTicketCreatedWrapper = useCallback(
    (ticketId: string) => {
      onValueChange(ticketId);
      globalModalStack.closeModal(MODAL_IDS.tickets.createQuick);
    },
    [onValueChange, globalModalStack]
  );

  const buildOptions = useCallback((tickets: TicketSummary[]) => {
    return tickets
      .filter((ticket) => ticket?.id)
      .map((ticket) => ({
        value: String(ticket.id),
        label: `${ticket.formatted_ticket_number || "TICKET"} - ${ticket.title || "Untitled"}`,
        searchableText: [
          ticket.title,
          ticket.formatted_ticket_number,
          ticket.contact_name,
          ticket.assigned_to_name,
        ]
          .filter(Boolean)
          .join(" "),
        status: ticket.status,
        priority: ticket.priority,
        contact_name: ticket.contact_name,
        assigned_to_name: ticket.assigned_to_name,
      }));
  }, []);

  const effectiveTickets = sourceTickets.length > 0 ? sourceTickets : (items || []);
  const options = useMemo(() => buildOptions(effectiveTickets), [buildOptions, effectiveTickets]);
  const ticketsLoading = hasSearchTerm ? ftsLoading : paginationLoading;
  const isLoading = (typeof isSearching === "boolean" ? isSearching : undefined) ?? ticketsLoading;

  const totalCount = ticketsPage?.count ?? 0;
  const totalPages = Math.ceil(totalCount / limit);
  const hasMore = !hasSearchTerm && page < totalPages;
  const isLoadingMore = !!isBackgroundFetching && page > 1 && !hasSearchTerm;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      setPage((p) => p + 1);
    }
  }, [hasMore, isLoadingMore]);

  const handleValueChange = useCallback(
    (val: string, option?: SelectOption) => {
      if (val === "__ADD_NEW__") {
        globalModalStack.openModal(MODAL_IDS.tickets.createQuick);
      } else {
        onValueChange(val, option);
      }
    },
    [onValueChange, globalModalStack]
  );

  const handleSearchChange = useCallback(
    (val: string) => {
      setTicketSearchTerm(val);
      onSearchChange?.(val);
    },
    [setTicketSearchTerm, onSearchChange]
  );

  const optionsWithAddNew = useMemo(
    () => [
      ...options,
      {
        value: "__ADD_NEW__",
        label: "Add New Ticket",
      },
    ],
    [options]
  );

  return (
    <>
      <div
        className={fullWidth ? "w-full flex items-center gap-2" : "flex items-center gap-2 w-full"}
      >
        <ResponsiveCommandSelect
          value={value}
          onValueChange={handleValueChange}
          options={optionsWithAddNew}
          placeholder={placeholder}
          disabled={disabled}
          searchValue={searchTermProp ?? ticketSearchTerm}
          onSearchChange={handleSearchChange}
          isSearching={!!isLoading}
          className="flex-1 min-w-0"
          enableRecents
          recentKey="tickets"
          recentMax={8}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          customOptionRenderer={renderTicketOption}
          expandInline={expandInline}
          expandInlineVariant={expandInlineVariant}
        />
      </div>
      <QuickTicketCreationModal />
    </>
  );
}
